import { useTranslation } from 'react-i18next';
import Input from '@/components/base/Input';
import Switch from '@/components/base/Switch';
import { systemSettings } from '@/mocks/settings';
import type { ItemServiceType } from '@/types';
import type { ServiceFormState } from './itemForm';

interface ServicePriceSectionProps {
  serviceKey: ItemServiceType;
  value: ServiceFormState;
  onChange: (next: ServiceFormState) => void;
  errors: { normal?: string; urgent?: string };
}

export default function ServicePriceSection({
  serviceKey,
  value,
  onChange,
  errors,
}: ServicePriceSectionProps) {
  const { t } = useTranslation();
  const disabled = !value.available;

  return (
    <div
      className={`rounded-md border p-3.5 transition-colors ${
        value.available ? 'border-background-200 bg-background-50' : 'border-background-200 bg-background-100/60'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-md ${
              value.available ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-400'
            }`}
          >
            <i
              className={`${
                serviceKey === 'wash_only'
                  ? 'ri-drop-line'
                  : serviceKey === 'iron_only'
                    ? 'ri-t-shirt-2-line'
                    : 'ri-shirt-line'
              } text-base leading-none`}
              aria-hidden="true"
            />
          </span>
          <h4 className="text-sm font-semibold text-foreground-900">{t(`items.service.${serviceKey}`)}</h4>
        </div>
        <Switch
          id={`svc-${serviceKey}-toggle`}
          checked={value.available}
          onChange={(checked) => onChange({ ...value, available: checked })}
          label={t('items.form.serviceAvailable')}
        />
      </div>

      <div className="mt-3.5 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <Input
          id={`svc-${serviceKey}-normal`}
          name={`${serviceKey}_normal`}
          label={t('items.form.normalPrice')}
          value={value.normal}
          disabled={disabled}
          inputMode="decimal"
          ltr
          placeholder="0.00"
          error={errors.normal}
          onChange={(e) => onChange({ ...value, normal: e.target.value })}
          trailing={<span className="px-2.5 text-xs text-foreground-500">{systemSettings.currency}</span>}
        />
        <Input
          id={`svc-${serviceKey}-urgent`}
          name={`${serviceKey}_urgent`}
          label={t('items.form.urgentPrice')}
          value={value.urgent}
          disabled={disabled}
          inputMode="decimal"
          ltr
          placeholder="0.00"
          error={errors.urgent}
          onChange={(e) => onChange({ ...value, urgent: e.target.value })}
          trailing={<span className="px-2.5 text-xs text-foreground-500">{systemSettings.currency}</span>}
        />
      </div>
    </div>
  );
}